import type { MessageKey } from '@ecom/i18n';

import type { MerchantOrder, OrderStatus } from '../orders/types';

/**
 * No merchant-facing read exists on the real `payments` table (DEV §6.6,
 * migration 0031): `app_user` holds no GRANT on it at all — confirmed by
 * reading the migration, which grants only `app_checkout` (write, at
 * checkout time) and RLS, nothing to the merchant role. Every payment in
 * this environment is COD (`payments_method_valid CHECK (method IN
 * ('cod'))`), and COD's own lifecycle needs no external call — it rides
 * entirely on the order's own state machine (migration 0031's own
 * header: "pending → pending_collection happens automatically... confirmed
 * happens on delivery"). This bucket is therefore derived from
 * `MerchantOrder.status`, the same real, already-fetched field every
 * other order screen uses — never a read of `payments` itself, and the
 * UI says so rather than implying more precision than this has.
 */
export type PaymentBucket = 'pending' | 'confirmed' | 'failed' | 'refunded' | 'cancelled';

export const PAYMENT_BUCKET_BY_STATUS: Record<OrderStatus, PaymentBucket> = {
  ORDER_CREATED: 'pending',
  PAYMENT_PENDING: 'pending',
  PAYMENT_CONFIRMED: 'confirmed',
  PAYMENT_FAILED: 'failed',
  PROCESSING: 'confirmed',
  PACKED: 'confirmed',
  CANCELLED: 'cancelled',
  SHIPPED: 'confirmed',
  DELIVERY_FAILED: 'confirmed',
  DELIVERED: 'confirmed',
  RETURN_REQUESTED: 'confirmed',
  RETURNED: 'confirmed',
  REFUNDED: 'refunded',
  PARTIALLY_REFUNDED: 'refunded',
};

export const PAYMENT_BUCKET_LABEL_KEY: Record<PaymentBucket, MessageKey> = {
  pending: 'merchant.payments.bucket.pending',
  confirmed: 'merchant.payments.bucket.confirmed',
  failed: 'merchant.payments.bucket.failed',
  refunded: 'merchant.payments.bucket.refunded',
  cancelled: 'merchant.payments.bucket.cancelled',
};

/**
 * Migration 0096 — a sale recorded by the merchant (`manual_sale`) is handed over at once, so its ORDER status says
 * nothing about the money: its bucket follows its own payment row — « confirmé » only when recorded as received,
 * otherwise still to collect. Every other order keeps the status-based bucket above, unchanged.
 */
export function paymentBucketOf(
  order: Pick<MerchantOrder, 'status' | 'source' | 'paymentStatus'>,
): PaymentBucket {
  if (order.source === 'manual_sale' && order.status !== 'CANCELLED') {
    if (order.paymentStatus === 'confirmed') return 'confirmed';
    if (order.paymentStatus === 'refunded' || order.paymentStatus === 'partially_refunded')
      return 'refunded';
    return 'pending';
  }
  return PAYMENT_BUCKET_BY_STATUS[order.status];
}
